import Errors from "./Errors"
import type { AxiosResponse } from "axios"

export default class Form<T extends Record<string, any>> {
  public errors: Errors<T>
  public originalData: T
  private _multipart: boolean
  public fileFields: (keyof T | string)[]
  private _creds: boolean
  private _precognition: boolean
  private _validateOnly: string | null
  public onProgressCallback: (e: any) => void

  constructor(data: T) {
    this.errors = new Errors<T>()
    this.originalData = data
    this._multipart = false
    this.fileFields = []
    this._creds = false
    this._precognition = false
    this._validateOnly = null
    this.onProgressCallback = (e) => {}

    for (let field in data) {
      if (Object.prototype.hasOwnProperty.call(data, field)) {
        ;(this as any)[field] = data[field]
      }
    }
  }

  withCredentials(): this {
    this._creds = true
    return this
  }

  withPrecognition(validateOnly: string | null = null): this {
    this._precognition = true
    this._validateOnly = validateOnly
    return this
  }

  clearPrecognition(): this {
    this._precognition = false
    this._validateOnly = null
    return this
  }

  /**
   * Set the form to response to multipart data.
   *
   * @param multipart
   */
  multipart(multipart?: boolean): this {
    this._multipart = typeof multipart !== "undefined" ? multipart : true
    return this
  }

  /**
   * Clear error of modified element.
   *
   * @param event
   */
  keydown(event: Event): void {
    const target = event.target as HTMLInputElement
    let name = target.name
    if (typeof name !== "undefined" && typeof this[name] !== "undefined") {
      this.errors.clear(name)
    }
  }

  onProgressChange(callback: (e: any) => void): void {
    if (typeof callback !== "function") {
      throw new Error("The function onProgressChange only accepts a function as a parameters")
    }

    this.onProgressCallback = callback
  }

  /**
   * Get the data for the form.
   *
   * @param {*|undefined} override
   */
  data(override: Partial<T> | null = null): T | FormData {
    let data: Record<string, any> = {}
    for (let field in this.originalData) {
      if (Object.prototype.hasOwnProperty.call(this.originalData, field)) {
        data[field] = (this as any)[field]
      }
    }

    if (override) {
      for (let field in override) {
        if (Object.prototype.hasOwnProperty.call(this.originalData, field)) {
          data[field] = override[field]
        }
      }
    }

    if (this._multipart) {
      let form = new FormData()

      for (let field in data) {
        if (Object.prototype.hasOwnProperty.call(data, field)) {
          if (this.isFileField(field)) {
            // Check if it is an array of files
            if (Array.isArray(data[field])) {
              // Make sure the files get recognized as an array of files
              data[field].map((item: any) => {
                let name = field + "[]"
                if (FileList && item instanceof FileList) {
                  form.append(name, item[0])
                } else {
                  form.append(name, item)
                }
              })
            } else {
              // It's not an array of files so deal with single file
              if (FileList && data[field] instanceof FileList) {
                form.append(field, data[field][0])
              } else {
                form.append(field, data[field])
              }
            }
          } else {
            let value = data[field]
            if (value === "null") {
              value = ""
            }
            form.append(field, value)
          }
        }
      }

      return form
    }

    // Add field name for single field validation if needed
    if (this._precognition && this._validateOnly) {
      data._field = this._validateOnly
    }

    return data as T
  }

  /**
   * check if the field is a file field.
   *
   * @private
   * @param field
   * @return {boolean}
   */
  isFileField(field: string): boolean {
    return this.fileFields.indexOf(field) > -1
  }

  /**
   * Resent form data.
   */
  reset(fields?: (keyof T)[]): void {
    for (let field in this.originalData) {
      if (Object.prototype.hasOwnProperty.call(this.originalData, field)) {
        if (typeof fields !== "undefined") {
          if (fields.indexOf(field as keyof T) > -1) {
            ;(this as any)[field] = this.originalData[field]
          }
        } else {
          ;(this as any)[field] = this.originalData[field]
        }
      }
    }

    this.errors.clearAll()
  }

  /**
   * Send a POST request.
   *
   * @param url
   * @param {any} override
   * @return {Promise<any>}
   */
  post(url: string, override: Partial<T> = {}): Promise<AxiosResponse> {
    return this.submit("post", url, override)
  }

  /**
   * Send a PUT request.
   *
   * @param url
   * @param override
   * @return {Promise<any>}
   */
  put(url: string, override: Partial<T> = {}): Promise<AxiosResponse> {
    return this.submit("put", url, override)
  }

  /**
   * Send a GET request.
   *
   * @param url
   * @param override
   * @return {Promise<any>}
   */
  get(url: string, override: Partial<T> = {}): Promise<AxiosResponse> {
    return this.submit("get", url, override)
  }

  /**
   * Send a DELETE request.
   *
   * @param url
   * @param override
   * @return {Promise<any>}
   */
  delete(url: string, override: Partial<T> = {}): Promise<AxiosResponse> {
    return this.submit("delete", url, override)
  }

  /**
   * Send a PATCH request.
   *
   * @param url
   * @param override
   * @return {Promise<any>}
   */
  patch(url: string, override: Partial<T> = {}): Promise<AxiosResponse> {
    return this.submit("patch", url, override)
  }

  /**
   * Get headers object.
   *
   * @private
   */
  headers(): Record<string, string> {
    let data: Record<string, string> = {}

    if (this._multipart) {
      data["Content-Type"] = "multipart/form-data"
    }

    if (this._precognition) {
      data["Precognition"] = "true"
      if (this._validateOnly) {
        data["Precognition-Validate-Only"] = this._validateOnly
      }
    }

    return data
  }

  /**
   * Let the form know that we have a file field.
   *
   * @param field
   */
  setAsFile(field: keyof T | string): this {
    if (!this.isFileField(field as string)) {
      this.multipart(true)
      this.fileFields.push(field)
    }

    return this
  }

  setAsNotFile(field: keyof T | string): this {
    this.fileFields = this.fileFields.filter((f) => f !== field)
    if (this.fileFields.length === 0) {
      this.multipart(false)
    }

    return this
  }

  /**
   * @private
   * @return {Object}
   */
  config(): { withCredentials: boolean; onUploadProgress: (e: any) => void } {
    return {
      withCredentials: this._creds,
      onUploadProgress: this.onProgressCallback,
    }
  }

  /**
   * @private
   * @param requestType
   * @param url
   * @param override
   * @return {Promise<any>}
   */
  submit(requestType: string, url: string, override: Partial<T>): Promise<AxiosResponse> {
    if (url.length === 0) {
      throw new Error("Please provide a valid URL")
    }

    if (this._multipart && requestType !== "post") {
      throw new Error(
        `Form is set to be multipart but request method is ${requestType}. Method should be changed to post.`,
      )
    }

    const headers = this.headers()
    const config = this.config()

    return new Promise((resolve, reject) => {
      const data = ["get"].includes(requestType) ? { params: this.data(override) } : this.data(override)
      axios[requestType](url, data, { ...config, headers })
        .then((response: AxiosResponse) => {
          resolve(response)
        })
        .catch((errors: any) => {
          this.onFail(errors)

          reject(errors)
        })
        .finally(() => {
          // clear precognition headers/values
          this.clearPrecognition()
        })
    })
  }

  /**
   * Handle errors.
   * @param errors
   * @private
   */
  onFail(errors: any): void {
    if (errors.response && errors.response.status === 422) {
      this.errors.set(errors.response.data)
    }
  }

  /**
   * Set default data.
   *
   * @param data
   */
  setDefault(data: Partial<T>): void {
    for (let field in this.originalData) {
      if (!Object.prototype.hasOwnProperty.call(this.originalData, field)) {
        return
      }

      if (typeof data[field] !== "undefined") {
        ;(this as any)[field] = data[field]
      }
    }
  }
}
